import $ from 'jquery';
import _ from 'underscore';
import Handlebars from 'handlebars';
import '../../vendor/select2';
import {translator} from '../../localization/translator';
import {getJsonScriptContent} from '../../utils/dom/getJsonScriptContent';
import '../plugins/elementEvents';

// The check for whether something is an object is a bit weird in
// JavaScript because arrays and functions are also objects.
function isPureObject(potentialObject) {
    return _.isObject(potentialObject) && !_.isArray(potentialObject) && !_.isFunction(potentialObject);
}

Handlebars.registerHelper('customEscapeMarkup', function (name) {
    // Do not try to escape the markup if it's not a string
    if (typeof name !== 'string') {
        return name;
    }

    // All HTML markup except for single quotes
    const replaceMap = {
        '\\': '&#92;',
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        '/': '&#47;'
    };

    // Escape HTML markup, except for single quotes
    return String(name).replace(/[&<>"\/\\]/g, function (match) {
        return replaceMap[match];
    });
})

/**
 * goes through an object that looks like this
 * {
 *     "name": null,
 *     "email": null,
 *     "location": {
 *         "name": null
 *     }
 *     "customer": {
 *         "id": ["f60dae2e-3487-4ad1-ac31-c69c1c858d5f"]
 *     }
 * }
 *
 * and populates it with a search term, replacing all the null values:
 * example search term "aalborg"
 *
 * {
 *     "name": "aalborg",
 *     "email": "aalborg",
 *     "location": {
 *         "name": "aalborg"
 *     }
 *     "customer": {
 *         "id": ["f60dae2e-3487-4ad1-ac31-c69c1c858d5f"]
 *     }
 * }
 *
 * @param queryObject
 * @param searchTerm
 * @returns {*}
 */
function addSearchToQueryObject(queryObject, searchTerm) {
    // iterate over the properties in the object
    for (const property in queryObject) {
        if (queryObject.hasOwnProperty(property)) {

            // check if the value is null, we only want to replace null values
            if (queryObject[property] === null) {
                queryObject[property] = searchTerm;

                // if it has a value, find out if the value is an object, because if so we want to also iterate over its properties
            } else if (isPureObject(queryObject[property])) {
                addSearchToQueryObject(queryObject[property], searchTerm);
            }
        }
    }

    return queryObject;
}

function renderOption(option, templateMethod) {
    if (option.id === '') {
        const text = option.text ?? $.fn.select2.defaults.placeholder;

        return $(`<span class="select2-placeholder">${text}</span>`);
    }

    const isPlaceholder = option.loading === true && option.disabled === true && option.text;
    const isRendered = option.element && option.element instanceof Option && option.text;

    if (isPlaceholder || isRendered) {
        return option.text;
    }

    return templateMethod(option);
}

function createRenderMethod(template) {
    const compiledTemplate = Handlebars.compile(template);

    return function(option) {
        return renderOption(option, compiledTemplate);
    };
}

function initializeSelect2() {
    const element = document.getElementById('select2-options');
    const {ajaxBaseUrl} = getJsonScriptContent(element);

    Object.assign($.fn.select2.defaults, {
        ajaxBaseUrl,
        formatNoMatches: translator.translate('ui.text.no_matches'),
        placeholder: translator.translate('ui.action.choose'),
    });

    $('select.select2', this).each(function() {
        const $this = $(this);

        const autofocus = $this.prop('autofocus');
        const multiple = $this.prop('multiple');
        const required = $this.prop('required');
        const ajax = $this.data('ajax');
        const description = $this.data('description');
        const dropdownCssClass = $this.data('hide-disabled') ? 'select2-hide-disabled' : '';
        const iconCssClass = $this.data('icon-class') || 'select2-icon';
        const placeholder = $this.data('placeholder') || '';

        if (autofocus) {
            // We handle focus after initialization
            $this.prop('autofocus', false);
        }

        function format(option) {
            const $element = $(option.element);

            const iconUrl = $element.data('iconUrl');
            const icon = iconUrl ? `<img src="${iconUrl}" class="${iconCssClass}"/>` : '';
            const text = $.fn.select2.defaults.defaults.escapeMarkup(option.text);

            return $(`<span>${icon}${text}</span>`);
        }

        if (ajax) {
            $this.select2({
                ajax: {
                    url: ajax,
                    dataType: 'json',
                    delay: 250,
                    placeholder: placeholder,
                    data(params) {
                        return {
                            q: params.term,
                        };
                    },
                    processResults(data) {
                        return {
                            results: data.results,
                        };
                    },
                },
                allowClear: !required,
                cache: true,
                closeOnSelect: !multiple,
                dropdownCssClass: dropdownCssClass,
                templateResult: format,
                templateSelection: format,
                minimumInputLength: 2,
                theme: 'bootstrap',
                width: '100%',
            });
        } else {
            $this.select2({
                allowClear: !required,
                closeOnSelect: !multiple,
                dropdownCssClass: dropdownCssClass,
                placeholder: placeholder,
                templateResult: format,
                templateSelection: format,
                theme: 'bootstrap',
                width: '100%',
            });
        }

        if (autofocus) {
            // This is a workaround for what seems to be a race condition
            // if Select2 is allow to focus during initialization
            setTimeout(() => $this.select2('focus'), 100);
        }

        if (description === 'below') {
            let $description = $(`${$this.attr('id')}_description`);

            if ($description.length === 0) {
                $description = $('<p/>').addClass('help-block');
                $this.parent().append($description);
            }

            const selected = $this.find(':selected');
            $description.text(selected.data('description'));

            $this.on('change', function() {
                const selected = $this.find(':selected');
                $description.text(selected.data('description'));
            });
        }
    });

    $('input[data-server-side]').each(function() {
        const $input = $(this);

        const renderOption = createRenderMethod($input.data('option-template'));
        const renderSelection = createRenderMethod($input.data('selection-template'));

        const isMultiSelect = $input.data('server-side-multiple') === true;
        const required = $input.prop('required');
        const rawValue = $input.val();
        const sort = $input.data('sort') || null;
        const sortDirection = $input.data('sort-direction') || null;
        // the variable idType is needed in the entity searcher
        const idType = 'uuid';

        if (!isMultiSelect && (!required || !rawValue)) {
            // Select2 doesn't support the allowClear setting on
            // single-selects without a placeholder option. We don't add
            // it if the select is optional or already has a value, as
            // there is no need to clear it in those cases.
            $input.append(new Option());
        }

        if (rawValue) {
            const parsedValue = JSON.parse(rawValue);

            if (isMultiSelect) {
                for (const value of parsedValue) {
                    $input.append(new Option(renderSelection(value), value.id, true, true));
                }
            } else {
                $input.append(new Option(renderSelection(parsedValue), parsedValue.id, true, true));
            }
        }

        // get the data we need to set up the select2
        const entityType = $input.data('entity-type');
        const pageSize = $input.data('server-side-page-size');
        const searchUrl = $.fn.select2.defaults.ajaxBaseUrl;
        const requiredFields = $input.data('required-fields') || {};
        const softDeleted = $input.data('soft-deleted') || 'exclude';
        const graphQL = $input.data('server-side-graphql') || false;

        if (!searchUrl) {
            throw new Error('fn.select2.defaults.ajaxBaseUrl must be set');
        }

        // Now, this bit is tricky
        // What we're doing here is taking the <input/> object and turning into a <select/> object
        // And we're doing this by creating a new <select/>, giving it all the attributes and children
        // and then doing the ol' switcheroo
        const contents = $input.contents();
        const attrs = {};

        // this grabs all the attributes (except 'type') and stores them
        $.each($input[0].attributes, function(idx, attr) {
            if (attr.nodeName !== 'type') {
                attrs[attr.nodeName] = attr.nodeValue;
            }
        });

        // this attribute is needed to make select2 multi-select
        if (isMultiSelect) {
            attrs['multiple'] = true;
        }

        // this creates the <select/> object, assigns the attribute and appends the children
        const $select = $('<select/>', attrs).append(contents);

        // also remember to make it a select2
        $select.addClass('select2');

        // now this is where we swap the <input/> with the <select/>
        // after this, the <input/> should no longer be in the DOM, but the <select/> should
        $input[0].parentNode.replaceChild($select[0], $input[0]);

        // since we still haven't initialized the select2 on the DOM element we need to do that
        $select.select2({
            allowClear: !required,
            closeOnSelect: !isMultiSelect,
            // An empty placeholder works around a bug with allowClear in Select2
            placeholder: '',
            minimumInputLength: 2,
            multiple: isMultiSelect,
            theme: 'bootstrap',
            // the ajax property defines how we fetch the data
            ajax: {
                url: searchUrl,
                type: 'POST',
                timeout: 10000,
                dataType: 'json',
                contentType: 'application/json',
                delay: 750,
                data(params) {
                    // first we need a copy of the required fields object, which defines the base query
                    // we want a copy so it isn't overwritten with each search
                    const query = _.clone(requiredFields);

                    // then we add the search term to every property without a value
                    // we don't overwrite existing values, because maybe the FormType has specific a restriction which we want to adhere to
                    addSearchToQueryObject(query, params.term);

                    // create the query object from that
                    // with the entityType as root type
                    const queryObj = {};
                    queryObj[entityType] = query;

                    // Prevent selected values from being included in the
                    // results, otherwise Select2 behaves strangely.
                    const excludedIds = _.pluck($select.select2('data'), 'id');

                    const payload = {
                        query: queryObj,
                        excludedIds: excludedIds,
                        length: pageSize,
                        softDeleted: softDeleted,
                        page: params.page || 1, // pages are 1-based
                        idType: idType,
                        graphQL: graphQL,
                    }

                    if(sort) {
                        payload['sort'] = sort;
                    }

                    if(sortDirection) {
                        payload['sortDirection'] = sortDirection;
                    }

                    // create a payload now that we have a query object
                    return JSON.stringify(payload);
                },
                processResults(response) {
                    // because we can't filter with entity searcher,
                    // we will do it here TODO make it better
                    response.data = response.data.filter(item => {
                        return item.anonymizedAt === null || !Object(item).hasOwnProperty('anonymizedAt');
                    })

                    return {
                        results: response.data,
                        pagination: {more: response.moreResults},
                    };
                },
            },
            templateResult: renderOption,
            templateSelection: renderSelection,
            width: '100%',
        });
    });
}

$(document).ready(() => {
    if (window.addElementReadyCallback) {
        addElementReadyCallback(initializeSelect2);
    } else {
        initializeSelect2.apply(document.body);
    }
});
